import { Bloc } from "@kernel/frontend-react-app";
import { LocalVideoState, defaultLocalVideoState } from "./LocalVideoState";
import { MainBloc } from "modules/main";
import {
  EnumerateMediaDeviceInteractor,
  StartMediaStreamInteractor
} from "./interactors";
import { ActiveDevice, Device, DeviceList } from "@domain";
import { UniqueEntityId } from "@kernel/ddd";
import { MediaDeviceService } from "./services";

export interface LocalVideoManager {
  change(
    videoElement: HTMLVideoElement,
    type: string,
    selectedDevice?: ActiveDevice,
    roomName?: string,
    displayName?: string
  ): Promise<void>;
  restart(type: "audio" | "video"): void;
  stop(type: "audio" | "video"): void;
}

export type LocalVideoBlocInteractors = {
  enumerateMediaDevice: EnumerateMediaDeviceInteractor;
  startMediaStream: StartMediaStreamInteractor;
};

export class LocalVideoBloc extends Bloc<LocalVideoState> {
  private mainBloc: MainBloc | null = null;
  private constructor(private interactors: LocalVideoBlocInteractors) {
    super(defaultLocalVideoState);
  }

  public setMainBloc(mainBloc: MainBloc): void {
    this.mainBloc = mainBloc;
  }

  public getMainBloc(): MainBloc {
    if (this.mainBloc === null) {
      throw new Error("MainBloc is not set");
    }
    return this.mainBloc;
  }

  public startLoading(): void {
    this.changeState({
      ...this.state,
      loading: true
    });
  }

  public stopLoading(): void {
    this.changeState({
      ...this.state,
      loading: false
    });
  }

  public async updateMediaDevices(
    skipPermissionRequest: boolean = false
  ): Promise<void> {
    let devices: DeviceList;
    if (skipPermissionRequest) {
      const mediaDevices =
        await new MediaDeviceService().enumerateWithoutPermission();
      const builtDevices = mediaDevices.map((deviceInfo) => {
        const id = new UniqueEntityId(deviceInfo.deviceId);
        return Device.create({ info: deviceInfo }, id);
      });
      devices = DeviceList.create(builtDevices);
    } else {
      devices = await this.interactors.enumerateMediaDevice.execute();
    }
    this.changeState({
      ...this.state,
      devices
    });
  }

  public selectAudioDevice(deviceId: string): void {
    const selectedDevice = ActiveDevice.create({
      ...this.state.selectedDevice.getProps(),
      audio: this.state.devices.getDeviceById(deviceId)
    });
    this.changeState({
      ...this.state,
      selectedDevice
    });
  }

  public selectVideoDevice(deviceId: string): void {
    const selectedDevice = ActiveDevice.create({
      ...this.state.selectedDevice.getProps(),
      video: this.state.devices.getDeviceById(deviceId)
    });
    this.changeState({
      ...this.state,
      selectedDevice
    });
  }

  public toggleVideo(): void {
    const selectedDevice = ActiveDevice.create({
      ...this.state.selectedDevice.getProps(),
      videoMuted: !this.state.selectedDevice.isVideoMuted()
    });
    this.changeState({
      ...this.state,
      selectedDevice
    });
  }

  public toggleAudio(): void {
    const selectedDevice = ActiveDevice.create({
      ...this.state.selectedDevice.getProps(),
      audioMuted: !this.state.selectedDevice.isAudioMuted()
    });
    this.changeState({
      ...this.state,
      selectedDevice
    });
  }

  public setActiveDevice(selectedDevice: ActiveDevice): void {
    this.changeState({
      ...this.state,
      selectedDevice
    });
  }

  public static create(): LocalVideoBloc {
    const interactors: LocalVideoBlocInteractors = {
      enumerateMediaDevice: EnumerateMediaDeviceInteractor.create(),
      startMediaStream: StartMediaStreamInteractor.create()
    };
    return new LocalVideoBloc(interactors);
  }
}
